/**
 * Permission Key Registry (frontend mirror of backend _4s_sdlc/rbac/permissions.py).
 *
 * SINGLE SOURCE OF TRUTH for permission KEY names. Guards, buttons and APIs reference
 * a permission by NAME directly — e.g. PERMISSIONS.APP_READ — never by resource+action.
 *
 * Backend convention: "<RESOURCE>.<ACTION>", ACTION ∈ {CREATE, READ, UPDATE, DELETE}.
 * Read-only resources (monitoring / log feeds) expose only "<RESOURCE>.READ".
 */
import { JOB_NAMES } from './jobNames';

export const PERMISSIONS = {
  // Platform-wide read: see every project's data, not only your own. What the
  // Data Explorer is gated on, server and client alike.
  CROSS_APP_READ: 'CROSS_APP.READ',
  /** Opens the Data Explorer. What it then shows is scoped per login: every
   *  project with CROSS_APP.READ, otherwise only the caller's own. */
  DATA_EXPLORER_READ: 'DATA_EXPLORER.READ',

  // Background Jobs — EXECUTE is deliberately separate from UPDATE so an operator
  // can re-run a job without being able to rewrite what it does.
  BACKGROUND_JOB_CREATE: 'BACKGROUND_JOB.CREATE',
  BACKGROUND_JOB_READ: 'BACKGROUND_JOB.READ',
  BACKGROUND_JOB_UPDATE: 'BACKGROUND_JOB.UPDATE',
  BACKGROUND_JOB_DELETE: 'BACKGROUND_JOB.DELETE',
  BACKGROUND_JOB_EXECUTE: 'BACKGROUND_JOB.EXECUTE',
  BACKGROUND_JOB_RUN_READ: 'BACKGROUND_JOB_RUN.READ',
  // A SEVENTH key the integration guide does not document: pausing a schedule is
  // gated separately from UPDATE so an on-call operator can stop a misbehaving job
  // without being able to rewrite it. Verified against the toggle endpoint.
  BACKGROUND_JOB_SCHEDULE_TOGGLE: 'BACKGROUND_JOB_SCHEDULE.TOGGLE',
  // Attaching a schedule to a job is a separate grant from editing the job:
  // one changes what runs, the other decides when.
  BACKGROUND_JOB_SCHEDULE: 'BACKGROUND_JOB.SCHEDULE',

  // Authority to edit/delete CORE (appId=null) records — the shared baseline every
  // project reads. Without it, core rows are shown read-only (disabled edit/delete).
  CORE_MANAGE: 'CORE.MANAGE',

  // Personnel & Users
  PERSONNEL_CREATE: 'PERSONNEL.CREATE',
  PERSONNEL_READ: 'PERSONNEL.READ',
  PERSONNEL_UPDATE: 'PERSONNEL.UPDATE',
  PERSONNEL_DELETE: 'PERSONNEL.DELETE',
  USER_CREATE: 'USER.CREATE',
  USER_READ: 'USER.READ',
  USER_UPDATE: 'USER.UPDATE',
  USER_DELETE: 'USER.DELETE',

  // Org structure
  UNIT_CREATE: 'UNIT.CREATE',
  UNIT_READ: 'UNIT.READ',
  UNIT_UPDATE: 'UNIT.UPDATE',
  UNIT_DELETE: 'UNIT.DELETE',
  UNIT_TYPE_CREATE: 'UNIT_TYPE.CREATE',
  UNIT_TYPE_READ: 'UNIT_TYPE.READ',
  UNIT_TYPE_UPDATE: 'UNIT_TYPE.UPDATE',
  UNIT_TYPE_DELETE: 'UNIT_TYPE.DELETE',
  UNIT_VILLAGE_CREATE: 'UNIT_VILLAGE.CREATE',
  UNIT_VILLAGE_READ: 'UNIT_VILLAGE.READ',
  UNIT_VILLAGE_UPDATE: 'UNIT_VILLAGE.UPDATE',
  UNIT_VILLAGE_DELETE: 'UNIT_VILLAGE.DELETE',
  DISTRICT_CREATE: 'DISTRICT.CREATE',
  DISTRICT_READ: 'DISTRICT.READ',
  DISTRICT_UPDATE: 'DISTRICT.UPDATE',
  DISTRICT_DELETE: 'DISTRICT.DELETE',
  DEPARTMENT_CREATE: 'DEPARTMENT.CREATE',
  DEPARTMENT_READ: 'DEPARTMENT.READ',
  DEPARTMENT_UPDATE: 'DEPARTMENT.UPDATE',
  DEPARTMENT_DELETE: 'DEPARTMENT.DELETE',
  DESIGNATION_CREATE: 'DESIGNATION.CREATE',
  DESIGNATION_READ: 'DESIGNATION.READ',
  DESIGNATION_UPDATE: 'DESIGNATION.UPDATE',
  DESIGNATION_DELETE: 'DESIGNATION.DELETE',
  MINISTRY_CREATE: 'MINISTRY.CREATE',
  MINISTRY_READ: 'MINISTRY.READ',
  MINISTRY_UPDATE: 'MINISTRY.UPDATE',
  MINISTRY_DELETE: 'MINISTRY.DELETE',

  // Apps & Data Sources
  APP_CREATE: 'APP.CREATE',
  APP_READ: 'APP.READ',
  APP_UPDATE: 'APP.UPDATE',
  APP_DELETE: 'APP.DELETE',
  APP_DEVICE_CREATE: 'APP_DEVICE.CREATE',
  APP_DEVICE_READ: 'APP_DEVICE.READ',
  APP_DEVICE_UPDATE: 'APP_DEVICE.UPDATE',
  APP_DEVICE_DELETE: 'APP_DEVICE.DELETE',
  APP_DATASOURCE_MANAGE: 'APP_DATASOURCE.MANAGE',
  APP_DATASOURCE_TEST: 'APP_DATASOURCE.TEST',
  // Per-project gateway + background-job settings, including their credentials.
  // Separate from APP.UPDATE, which only means 'rename a project'.
  APP_INTEGRATION_MANAGE: 'APP_INTEGRATION.MANAGE',

  // Application configuration — the typed, per-project registry that now holds
  // the background-job tunables and channel gateway settings. DELETE gates
  // DEACTIVATION: configuration is deactivated, never removed, so its history stays
  // answerable.
  APP_CONFIG_CREATE: 'APP_CONFIG.CREATE',
  APP_CONFIG_READ: 'APP_CONFIG.READ',
  APP_SETTING_READ: 'APP_SETTING.READ',
  APP_SETTING_UPDATE: 'APP_SETTING.UPDATE',
  APP_CONFIG_UPDATE: 'APP_CONFIG.UPDATE',
  APP_CONFIG_DELETE: 'APP_CONFIG.DELETE',

  // Configuration & content masters
  VALUE_SET_CREATE: 'VALUE_SET.CREATE',
  VALUE_SET_READ: 'VALUE_SET.READ',
  VALUE_SET_UPDATE: 'VALUE_SET.UPDATE',
  VALUE_SET_DELETE: 'VALUE_SET.DELETE',
  ERROR_MASTER_CREATE: 'ERROR_MASTER.CREATE',
  ERROR_MASTER_READ: 'ERROR_MASTER.READ',
  ERROR_MASTER_UPDATE: 'ERROR_MASTER.UPDATE',
  ERROR_MASTER_DELETE: 'ERROR_MASTER.DELETE',
  LOG_MASTER_CREATE: 'LOG_MASTER.CREATE',
  LOG_MASTER_READ: 'LOG_MASTER.READ',
  LOG_MASTER_UPDATE: 'LOG_MASTER.UPDATE',
  LOG_MASTER_DELETE: 'LOG_MASTER.DELETE',
  FEEDBACK_MASTER_CREATE: 'FEEDBACK_MASTER.CREATE',
  FEEDBACK_MASTER_READ: 'FEEDBACK_MASTER.READ',
  FEEDBACK_MASTER_UPDATE: 'FEEDBACK_MASTER.UPDATE',
  FEEDBACK_MASTER_DELETE: 'FEEDBACK_MASTER.DELETE',
  // A submission is written by an END USER answering a form, so CREATE here is
  // not an admin key. READ is "my own"; READ_ALL is every submission in the project.
  FEEDBACK_SUBMISSION_CREATE: 'FEEDBACK_SUBMISSION.CREATE',
  FEEDBACK_SUBMISSION_READ: 'FEEDBACK_SUBMISSION.READ',
  FEEDBACK_SUBMISSION_READ_ALL: 'FEEDBACK_SUBMISSION.READ_ALL',
  FEEDBACK_SUBMISSION_UPDATE: 'FEEDBACK_SUBMISSION.UPDATE',
  FEEDBACK_SUBMISSION_DELETE: 'FEEDBACK_SUBMISSION.DELETE',
  FEEDBACK_SUBMISSION_MANAGE: 'FEEDBACK_SUBMISSION.MANAGE',
  // A review row is created by the SERVER when feedback arrives, never by a
  // person — hence no CREATE key. OVERRIDE is reviewing something not assigned to you.
  FEEDBACK_REVIEW_READ: 'FEEDBACK_REVIEW.READ',
  FEEDBACK_REVIEW_UPDATE: 'FEEDBACK_REVIEW.UPDATE',
  FEEDBACK_REVIEW_OVERRIDE: 'FEEDBACK_REVIEW.OVERRIDE',
  FEEDBACK_REVIEW_DELETE: 'FEEDBACK_REVIEW.DELETE',
  NOTIFICATION_MASTER_CREATE: 'NOTIFICATION_MASTER.CREATE',
  NOTIFICATION_MASTER_READ: 'NOTIFICATION_MASTER.READ',
  NOTIFICATION_MASTER_UPDATE: 'NOTIFICATION_MASTER.UPDATE',
  NOTIFICATION_MASTER_DELETE: 'NOTIFICATION_MASTER.DELETE',
  // Publishing a notification and attaching a schedule to it are separate
  // grants: one makes a configuration live, the other decides when it fires.
  NOTIFICATION_MASTER_SCHEDULE: 'NOTIFICATION_MASTER.SCHEDULE',

  // Notification engine configuration masters.
  NOTIFICATION_TYPE_CREATE: 'NOTIFICATION_TYPE.CREATE',
  NOTIFICATION_TYPE_READ: 'NOTIFICATION_TYPE.READ',
  NOTIFICATION_TYPE_UPDATE: 'NOTIFICATION_TYPE.UPDATE',
  NOTIFICATION_TYPE_DELETE: 'NOTIFICATION_TYPE.DELETE',
  RECIPIENT_MASTER_CREATE: 'RECIPIENT_MASTER.CREATE',
  RECIPIENT_MASTER_READ: 'RECIPIENT_MASTER.READ',
  RECIPIENT_MASTER_UPDATE: 'RECIPIENT_MASTER.UPDATE',
  RECIPIENT_MASTER_DELETE: 'RECIPIENT_MASTER.DELETE',
  RECIPIENT_LIST_CREATE: 'RECIPIENT_LIST.CREATE',
  RECIPIENT_LIST_READ: 'RECIPIENT_LIST.READ',
  RECIPIENT_LIST_UPDATE: 'RECIPIENT_LIST.UPDATE',
  RECIPIENT_LIST_DELETE: 'RECIPIENT_LIST.DELETE',
  ACTION_MASTER_CREATE: 'ACTION_MASTER.CREATE',
  ACTION_MASTER_READ: 'ACTION_MASTER.READ',
  ACTION_MASTER_UPDATE: 'ACTION_MASTER.UPDATE',
  ACTION_MASTER_DELETE: 'ACTION_MASTER.DELETE',
  SCHEDULE_CREATE: 'SCHEDULE.CREATE',
  SCHEDULE_READ: 'SCHEDULE.READ',
  SCHEDULE_UPDATE: 'SCHEDULE.UPDATE',
  SCHEDULE_DELETE: 'SCHEDULE.DELETE',
  // Publishing an event onto the bus, which is what a notification fires from.
  NOTIFICATION_EVENT_PUBLISH: 'NOTIFICATION_EVENT.PUBLISH',
  PROMPT_CREATE: 'PROMPT.CREATE',
  PROMPT_READ: 'PROMPT.READ',
  PROMPT_UPDATE: 'PROMPT.UPDATE',
  PROMPT_DELETE: 'PROMPT.DELETE',
  AI_MODEL_CREATE: 'AI_MODEL.CREATE',
  AI_MODEL_READ: 'AI_MODEL.READ',
  AI_MODEL_UPDATE: 'AI_MODEL.UPDATE',
  AI_MODEL_DELETE: 'AI_MODEL.DELETE',
  AGENT_CREATE: 'AGENT.CREATE',
  AGENT_READ: 'AGENT.READ',
  AGENT_UPDATE: 'AGENT.UPDATE',
  AGENT_DELETE: 'AGENT.DELETE',
  // Conversations (multi-turn chat on a conversational agent). EXECUTE gates
  // sending a message, because that is the action that spends tokens.
  CONVERSATION_CREATE: 'CONVERSATION.CREATE',
  CONVERSATION_READ: 'CONVERSATION.READ',
  CONVERSATION_UPDATE: 'CONVERSATION.UPDATE',
  CONVERSATION_DELETE: 'CONVERSATION.DELETE',
  CONVERSATION_EXECUTE: 'CONVERSATION.EXECUTE',
  EVAL_CREATE: 'EVAL.CREATE',
  EVAL_READ: 'EVAL.READ',
  EVAL_UPDATE: 'EVAL.UPDATE',
  EVAL_DELETE: 'EVAL.DELETE',
  // Normalized eval model: the case library and the suites are gated separately.
  EVAL_CASE_CREATE: 'EVAL_CASE.CREATE',
  EVAL_CASE_READ: 'EVAL_CASE.READ',
  EVAL_CASE_UPDATE: 'EVAL_CASE.UPDATE',
  EVAL_CASE_DELETE: 'EVAL_CASE.DELETE',
  FUNCTION_MASTER_CREATE: 'FUNCTION_MASTER.CREATE',
  FUNCTION_MASTER_READ: 'FUNCTION_MASTER.READ',
  FUNCTION_MASTER_UPDATE: 'FUNCTION_MASTER.UPDATE',
  FUNCTION_MASTER_DELETE: 'FUNCTION_MASTER.DELETE',
  FUNCTION_MASTER_TEST: 'FUNCTION_MASTER.TEST',
  EVAL_SUITE_CREATE: 'EVAL_SUITE.CREATE',
  EVAL_SUITE_READ: 'EVAL_SUITE.READ',
  EVAL_SUITE_UPDATE: 'EVAL_SUITE.UPDATE',
  EVAL_SUITE_DELETE: 'EVAL_SUITE.DELETE',
  // Knowledge Base Component — the corpus. Documents ride on KNOWLEDGE_BASE.UPDATE,
  // the way a pipeline's sources ride on PIPELINE.UPDATE. VERIFY is separate: it is
  // what clears a staleness warning, so it must not come free with rename rights.
  KNOWLEDGE_BASE_CREATE: 'KNOWLEDGE_BASE.CREATE',
  KNOWLEDGE_BASE_READ: 'KNOWLEDGE_BASE.READ',
  KNOWLEDGE_BASE_UPDATE: 'KNOWLEDGE_BASE.UPDATE',
  KNOWLEDGE_BASE_DELETE: 'KNOWLEDGE_BASE.DELETE',
  KNOWLEDGE_BASE_VERIFY: 'KNOWLEDGE_BASE.VERIFY',

  // Pipeline Core Component. Running spends money at the embedding provider and
  // rewrites an index; retiring changes what production can roll back to.
  PIPELINE_CREATE: 'PIPELINE.CREATE',
  PIPELINE_READ: 'PIPELINE.READ',
  PIPELINE_UPDATE: 'PIPELINE.UPDATE',
  PIPELINE_DELETE: 'PIPELINE.DELETE',
  PIPELINE_RUN_READ: 'PIPELINE_RUN.READ',
  PIPELINE_RUN_EXECUTE: 'PIPELINE_RUN.EXECUTE',
  PIPELINE_ARTIFACT_READ: 'PIPELINE_ARTIFACT.READ',
  PIPELINE_ARTIFACT_RETIRE: 'PIPELINE_ARTIFACT.RETIRE',
  // RAG Core Component. QUERY spends money (embedding + answer model); ACTIVATE
  // changes what production answers with.
  RAG_CREATE: 'RAG.CREATE',
  RAG_READ: 'RAG.READ',
  RAG_UPDATE: 'RAG.UPDATE',
  RAG_DELETE: 'RAG.DELETE',
  RAG_QUERY: 'RAG.QUERY',
  RAG_VERSION_ACTIVATE: 'RAG_VERSION.ACTIVATE',
  // AI execution — EXECUTE permissions are separate from CRUD so an operator can
  // run a stored prompt / trigger an eval run without being able to edit them.
  PROMPT_EXECUTION_EXECUTE: 'PROMPT_EXECUTION.EXECUTE',
  EVAL_RUN_EXECUTE: 'EVAL_RUN.EXECUTE',
  EVAL_RUN_READ: 'EVAL_RUN.READ',

  // Access Control (RBAC)
  AUTH_PERMISSION_CREATE: 'AUTH_PERMISSION.CREATE',
  AUTH_PERMISSION_READ: 'AUTH_PERMISSION.READ',
  AUTH_PERMISSION_UPDATE: 'AUTH_PERMISSION.UPDATE',
  AUTH_PERMISSION_DELETE: 'AUTH_PERMISSION.DELETE',
  AUTH_PERMISSION_SET_CREATE: 'AUTH_PERMISSION_SET.CREATE',
  AUTH_PERMISSION_SET_READ: 'AUTH_PERMISSION_SET.READ',
  AUTH_PERMISSION_SET_UPDATE: 'AUTH_PERMISSION_SET.UPDATE',
  AUTH_PERMISSION_SET_DELETE: 'AUTH_PERMISSION_SET.DELETE',
  AUTH_ROLE_CREATE: 'AUTH_ROLE.CREATE',
  AUTH_ROLE_READ: 'AUTH_ROLE.READ',
  AUTH_ROLE_UPDATE: 'AUTH_ROLE.UPDATE',
  AUTH_ROLE_DELETE: 'AUTH_ROLE.DELETE',
  AUTH_ROLE_BINDING_CREATE: 'AUTH_ROLE_BINDING.CREATE',
  AUTH_ROLE_BINDING_READ: 'AUTH_ROLE_BINDING.READ',
  AUTH_ROLE_BINDING_UPDATE: 'AUTH_ROLE_BINDING.UPDATE',
  AUTH_ROLE_BINDING_DELETE: 'AUTH_ROLE_BINDING.DELETE',
  AUTH_USER_ROLE_ASSIGNMENT_CREATE: 'AUTH_USER_ROLE_ASSIGNMENT.CREATE',
  AUTH_USER_ROLE_ASSIGNMENT_READ: 'AUTH_USER_ROLE_ASSIGNMENT.READ',
  AUTH_USER_ROLE_ASSIGNMENT_UPDATE: 'AUTH_USER_ROLE_ASSIGNMENT.UPDATE',
  AUTH_USER_ROLE_ASSIGNMENT_DELETE: 'AUTH_USER_ROLE_ASSIGNMENT.DELETE',

  // Partner integrations (declared statically with full literal names)
  PARTNER_CREATE: 'PARTNER.CREATE',
  PARTNER_READ: 'PARTNER.READ',
  PARTNER_UPDATE: 'PARTNER.UPDATE',
  PARTNER_DELETE: 'PARTNER.DELETE',
  INTEGRATION_CONFIG_CREATE: 'INTEGRATION_CONFIG.CREATE',
  INTEGRATION_CONFIG_READ: 'INTEGRATION_CONFIG.READ',
  INTEGRATION_CONFIG_UPDATE: 'INTEGRATION_CONFIG.UPDATE',
  INTEGRATION_CONFIG_DELETE: 'INTEGRATION_CONFIG.DELETE',
  PARTNER_CONNECTION_CREATE: 'PARTNER_CONNECTION.CREATE',
  PARTNER_CONNECTION_READ: 'PARTNER_CONNECTION.READ',
  PARTNER_CONNECTION_UPDATE: 'PARTNER_CONNECTION.UPDATE',
  PARTNER_CONNECTION_DELETE: 'PARTNER_CONNECTION.DELETE',
  PARTNER_CONNECTION_TEST: 'PARTNER_CONNECTION.TEST',
  CONNECTION_TYPE_CREATE: 'CONNECTION_TYPE.CREATE',
  CONNECTION_TYPE_READ: 'CONNECTION_TYPE.READ',
  CONNECTION_TYPE_UPDATE: 'CONNECTION_TYPE.UPDATE',
  CONNECTION_TYPE_DELETE: 'CONNECTION_TYPE.DELETE',

  // Monitoring / read-only feeds
  DASHBOARD_READ: 'DASHBOARD.READ',
  ERROR_LOG_READ: 'ERROR_LOG.READ',
  // Acknowledging an error changes its status — a distinct write from reading it.
  ERROR_LOG_ACKNOWLEDGE: 'ERROR_LOG.ACKNOWLEDGE',
  LOG_TRANSACTION_READ: 'LOG_TRANSACTION.READ',
  PROMPT_EXECUTION_READ: 'PROMPT_EXECUTION.READ',
  NOTIFICATION_READ: 'NOTIFICATION.READ',
  ORG_STRUCTURE_READ: 'ORG_STRUCTURE.READ',

  // Error → notification alert bindings (create one to make an error start paging).
  ERROR_ALERT_BINDING_CREATE: 'ERROR_ALERT_BINDING.CREATE',
  ERROR_ALERT_BINDING_READ: 'ERROR_ALERT_BINDING.READ',
  ERROR_ALERT_BINDING_UPDATE: 'ERROR_ALERT_BINDING.UPDATE',
  ERROR_ALERT_BINDING_DELETE: 'ERROR_ALERT_BINDING.DELETE',

  // Uploaded files
  FILE_UPLOAD_CREATE: 'FILE_UPLOAD.CREATE',
  FILE_UPLOAD_READ: 'FILE_UPLOAD.READ',
  FILE_UPLOAD_UPDATE: 'FILE_UPLOAD.UPDATE',
  FILE_UPLOAD_DELETE: 'FILE_UPLOAD.DELETE',
} as const;

export type PermissionKey = typeof PERMISSIONS[keyof typeof PERMISSIONS];
/** Capability accessors used by the convenience hooks. */
export type PermissionAction = 'create' | 'read' | 'update' | 'delete';

export interface CrudPermissions {
  create?: string;
  read?: string;
  update?: string;
  delete?: string;
}

/**
 * Permission keys grouped per screen (job) — used by the convenience useCan* hooks
 * and the menu. Every value is a named PERMISSIONS entry (no inline strings).
 */
export const JOB_PERMISSIONS: Record<string, CrudPermissions> = {
  // Background jobs
  [JOB_NAMES.BACKGROUND_JOB]: { create: PERMISSIONS.BACKGROUND_JOB_CREATE, read: PERMISSIONS.BACKGROUND_JOB_READ, update: PERMISSIONS.BACKGROUND_JOB_UPDATE, delete: PERMISSIONS.BACKGROUND_JOB_DELETE },
  // run history is read-only — the engine writes it
  [JOB_NAMES.BACKGROUND_JOB_RUN]: { read: PERMISSIONS.BACKGROUND_JOB_RUN_READ },

  // Masters
  [JOB_NAMES.APPS]: { create: PERMISSIONS.APP_CREATE, read: PERMISSIONS.APP_READ, update: PERMISSIONS.APP_UPDATE, delete: PERMISSIONS.APP_DELETE },
  [JOB_NAMES.APP_CONFIG]: { create: PERMISSIONS.APP_CONFIG_CREATE, read: PERMISSIONS.APP_CONFIG_READ, update: PERMISSIONS.APP_CONFIG_UPDATE, delete: PERMISSIONS.APP_CONFIG_DELETE },
  [JOB_NAMES.DEPARTMENTS]: { create: PERMISSIONS.DEPARTMENT_CREATE, read: PERMISSIONS.DEPARTMENT_READ, update: PERMISSIONS.DEPARTMENT_UPDATE, delete: PERMISSIONS.DEPARTMENT_DELETE },
  [JOB_NAMES.DESIGNATION_MASTER]: { create: PERMISSIONS.DESIGNATION_CREATE, read: PERMISSIONS.DESIGNATION_READ, update: PERMISSIONS.DESIGNATION_UPDATE, delete: PERMISSIONS.DESIGNATION_DELETE },
  [JOB_NAMES.DISTRICTS]: { create: PERMISSIONS.DISTRICT_CREATE, read: PERMISSIONS.DISTRICT_READ, update: PERMISSIONS.DISTRICT_UPDATE, delete: PERMISSIONS.DISTRICT_DELETE },
  [JOB_NAMES.UNIT_TYPE]: { create: PERMISSIONS.UNIT_TYPE_CREATE, read: PERMISSIONS.UNIT_TYPE_READ, update: PERMISSIONS.UNIT_TYPE_UPDATE, delete: PERMISSIONS.UNIT_TYPE_DELETE },
  [JOB_NAMES.MINISTRIES]: { create: PERMISSIONS.MINISTRY_CREATE, read: PERMISSIONS.MINISTRY_READ, update: PERMISSIONS.MINISTRY_UPDATE, delete: PERMISSIONS.MINISTRY_DELETE },
  [JOB_NAMES.PERSONNEL]: { create: PERMISSIONS.PERSONNEL_CREATE, read: PERMISSIONS.PERSONNEL_READ, update: PERMISSIONS.PERSONNEL_UPDATE, delete: PERMISSIONS.PERSONNEL_DELETE },
  [JOB_NAMES.USER_MANAGEMENT]: { create: PERMISSIONS.USER_CREATE, read: PERMISSIONS.USER_READ, update: PERMISSIONS.USER_UPDATE, delete: PERMISSIONS.USER_DELETE },
  [JOB_NAMES.UNITS]: { create: PERMISSIONS.UNIT_CREATE, read: PERMISSIONS.UNIT_READ, update: PERMISSIONS.UNIT_UPDATE, delete: PERMISSIONS.UNIT_DELETE },
  [JOB_NAMES.UNIT_VILLAGES]: { create: PERMISSIONS.UNIT_VILLAGE_CREATE, read: PERMISSIONS.UNIT_VILLAGE_READ, update: PERMISSIONS.UNIT_VILLAGE_UPDATE, delete: PERMISSIONS.UNIT_VILLAGE_DELETE },
  [JOB_NAMES.VALUE_SETS]: { create: PERMISSIONS.VALUE_SET_CREATE, read: PERMISSIONS.VALUE_SET_READ, update: PERMISSIONS.VALUE_SET_UPDATE, delete: PERMISSIONS.VALUE_SET_DELETE },
  [JOB_NAMES.ERROR_MASTER]: { create: PERMISSIONS.ERROR_MASTER_CREATE, read: PERMISSIONS.ERROR_MASTER_READ, update: PERMISSIONS.ERROR_MASTER_UPDATE, delete: PERMISSIONS.ERROR_MASTER_DELETE },
  [JOB_NAMES.LOG_MASTER]: { create: PERMISSIONS.LOG_MASTER_CREATE, read: PERMISSIONS.LOG_MASTER_READ, update: PERMISSIONS.LOG_MASTER_UPDATE, delete: PERMISSIONS.LOG_MASTER_DELETE },
  [JOB_NAMES.FEEDBACK_MASTER]: { create: PERMISSIONS.FEEDBACK_MASTER_CREATE, read: PERMISSIONS.FEEDBACK_MASTER_READ, update: PERMISSIONS.FEEDBACK_MASTER_UPDATE, delete: PERMISSIONS.FEEDBACK_MASTER_DELETE },
  [JOB_NAMES.FEEDBACK_SUBMISSION]: { create: PERMISSIONS.FEEDBACK_SUBMISSION_CREATE, read: PERMISSIONS.FEEDBACK_SUBMISSION_READ_ALL, update: PERMISSIONS.FEEDBACK_SUBMISSION_UPDATE, delete: PERMISSIONS.FEEDBACK_SUBMISSION_DELETE },
  [JOB_NAMES.FEEDBACK_REVIEW]: { create: PERMISSIONS.FEEDBACK_REVIEW_UPDATE, read: PERMISSIONS.FEEDBACK_REVIEW_READ, update: PERMISSIONS.FEEDBACK_REVIEW_UPDATE, delete: PERMISSIONS.FEEDBACK_REVIEW_DELETE },
  [JOB_NAMES.NOTIFICATION_MASTER]: { create: PERMISSIONS.NOTIFICATION_MASTER_CREATE, read: PERMISSIONS.NOTIFICATION_MASTER_READ, update: PERMISSIONS.NOTIFICATION_MASTER_UPDATE, delete: PERMISSIONS.NOTIFICATION_MASTER_DELETE },
  [JOB_NAMES.PROMPT_MASTER]: { create: PERMISSIONS.PROMPT_CREATE, read: PERMISSIONS.PROMPT_READ, update: PERMISSIONS.PROMPT_UPDATE, delete: PERMISSIONS.PROMPT_DELETE },
  [JOB_NAMES.AI_MODEL_MASTER]: { create: PERMISSIONS.AI_MODEL_CREATE, read: PERMISSIONS.AI_MODEL_READ, update: PERMISSIONS.AI_MODEL_UPDATE, delete: PERMISSIONS.AI_MODEL_DELETE },
  [JOB_NAMES.AGENT]: { create: PERMISSIONS.AGENT_CREATE, read: PERMISSIONS.AGENT_READ, update: PERMISSIONS.AGENT_UPDATE, delete: PERMISSIONS.AGENT_DELETE },
  [JOB_NAMES.CONVERSATIONS]: { create: PERMISSIONS.CONVERSATION_CREATE, read: PERMISSIONS.CONVERSATION_READ, update: PERMISSIONS.CONVERSATION_UPDATE, delete: PERMISSIONS.CONVERSATION_DELETE },
  // The "Evals" screen is the suite screen in the normalized model.
  [JOB_NAMES.EVALS]: { create: PERMISSIONS.EVAL_SUITE_CREATE, read: PERMISSIONS.EVAL_SUITE_READ, update: PERMISSIONS.EVAL_SUITE_UPDATE, delete: PERMISSIONS.EVAL_SUITE_DELETE },
  [JOB_NAMES.EVAL_CASE]: { create: PERMISSIONS.EVAL_CASE_CREATE, read: PERMISSIONS.EVAL_CASE_READ, update: PERMISSIONS.EVAL_CASE_UPDATE, delete: PERMISSIONS.EVAL_CASE_DELETE },
  [JOB_NAMES.FUNCTION_MASTER]: { create: PERMISSIONS.FUNCTION_MASTER_CREATE, read: PERMISSIONS.FUNCTION_MASTER_READ, update: PERMISSIONS.FUNCTION_MASTER_UPDATE, delete: PERMISSIONS.FUNCTION_MASTER_DELETE },
  // Eval runs history is read-only from the UI — the engine writes the run records.
  [JOB_NAMES.EVAL_RUNS]: { read: PERMISSIONS.EVAL_RUN_READ },
  [JOB_NAMES.KNOWLEDGE_BASE]: { create: PERMISSIONS.KNOWLEDGE_BASE_CREATE, read: PERMISSIONS.KNOWLEDGE_BASE_READ, update: PERMISSIONS.KNOWLEDGE_BASE_UPDATE, delete: PERMISSIONS.KNOWLEDGE_BASE_DELETE },
  [JOB_NAMES.PIPELINE]: { create: PERMISSIONS.PIPELINE_CREATE, read: PERMISSIONS.PIPELINE_READ, update: PERMISSIONS.PIPELINE_UPDATE, delete: PERMISSIONS.PIPELINE_DELETE },
  // Run history is read-only from the UI — the engine writes it; triggering is PIPELINE_RUN.EXECUTE.
  [JOB_NAMES.PIPELINE_RUNS]: { read: PERMISSIONS.PIPELINE_RUN_READ },
  [JOB_NAMES.RAG]: { create: PERMISSIONS.RAG_CREATE, read: PERMISSIONS.RAG_READ, update: PERMISSIONS.RAG_UPDATE, delete: PERMISSIONS.RAG_DELETE },

  // Access Control (RBAC)
  [JOB_NAMES.ROLES]: { create: PERMISSIONS.AUTH_ROLE_CREATE, read: PERMISSIONS.AUTH_ROLE_READ, update: PERMISSIONS.AUTH_ROLE_UPDATE, delete: PERMISSIONS.AUTH_ROLE_DELETE },
  [JOB_NAMES.SYSTEM_ROLES]: { create: PERMISSIONS.AUTH_ROLE_CREATE, read: PERMISSIONS.AUTH_ROLE_READ, update: PERMISSIONS.AUTH_ROLE_UPDATE, delete: PERMISSIONS.AUTH_ROLE_DELETE },
  [JOB_NAMES.PERMISSIONS]: { create: PERMISSIONS.AUTH_PERMISSION_CREATE, read: PERMISSIONS.AUTH_PERMISSION_READ, update: PERMISSIONS.AUTH_PERMISSION_UPDATE, delete: PERMISSIONS.AUTH_PERMISSION_DELETE },
  [JOB_NAMES.PERMISSION_MAPPINGS]: { create: PERMISSIONS.AUTH_PERMISSION_SET_CREATE, read: PERMISSIONS.AUTH_PERMISSION_SET_READ, update: PERMISSIONS.AUTH_PERMISSION_SET_UPDATE, delete: PERMISSIONS.AUTH_PERMISSION_SET_DELETE },
  [JOB_NAMES.USER_ROLE_PERMISSIONS]: { create: PERMISSIONS.AUTH_USER_ROLE_ASSIGNMENT_CREATE, read: PERMISSIONS.AUTH_USER_ROLE_ASSIGNMENT_READ, update: PERMISSIONS.AUTH_USER_ROLE_ASSIGNMENT_UPDATE, delete: PERMISSIONS.AUTH_USER_ROLE_ASSIGNMENT_DELETE },

  // Partner integrations
  [JOB_NAMES.PARTNERS]: { create: PERMISSIONS.PARTNER_CREATE, read: PERMISSIONS.PARTNER_READ, update: PERMISSIONS.PARTNER_UPDATE, delete: PERMISSIONS.PARTNER_DELETE },
  [JOB_NAMES.PARTNER_CONNECTIONS]: { create: PERMISSIONS.PARTNER_CONNECTION_CREATE, read: PERMISSIONS.PARTNER_CONNECTION_READ, update: PERMISSIONS.PARTNER_CONNECTION_UPDATE, delete: PERMISSIONS.PARTNER_CONNECTION_DELETE },
  [JOB_NAMES.CONNECTION_TYPES]: { create: PERMISSIONS.CONNECTION_TYPE_CREATE, read: PERMISSIONS.CONNECTION_TYPE_READ, update: PERMISSIONS.CONNECTION_TYPE_UPDATE, delete: PERMISSIONS.CONNECTION_TYPE_DELETE },
  [JOB_NAMES.SMS_TEMPLATES]: { create: PERMISSIONS.INTEGRATION_CONFIG_CREATE, read: PERMISSIONS.INTEGRATION_CONFIG_READ, update: PERMISSIONS.INTEGRATION_CONFIG_UPDATE, delete: PERMISSIONS.INTEGRATION_CONFIG_DELETE },

  // Monitoring / read-only feeds
  [JOB_NAMES.ERROR_LOGS]: { read: PERMISSIONS.ERROR_LOG_READ },
  [JOB_NAMES.FILE_UPLOAD]: { create: PERMISSIONS.FILE_UPLOAD_CREATE, read: PERMISSIONS.FILE_UPLOAD_READ, update: PERMISSIONS.FILE_UPLOAD_UPDATE, delete: PERMISSIONS.FILE_UPLOAD_DELETE },
  [JOB_NAMES.LOG_TRANSACTION]: { read: PERMISSIONS.LOG_TRANSACTION_READ },
  [JOB_NAMES.PROMPT_EXECUTIONS]: { read: PERMISSIONS.PROMPT_EXECUTION_READ },
  [JOB_NAMES.NOTIFICATIONS]: { read: PERMISSIONS.NOTIFICATION_READ },

  // Notification engine. NOTIFICATION_RUNS is read-only by design — a fire is a
  // record of what happened, so it is cancelled or replayed, never edited.
  [JOB_NAMES.NOTIFICATION_TYPE]: { create: PERMISSIONS.NOTIFICATION_TYPE_CREATE, read: PERMISSIONS.NOTIFICATION_TYPE_READ, update: PERMISSIONS.NOTIFICATION_TYPE_UPDATE, delete: PERMISSIONS.NOTIFICATION_TYPE_DELETE },
  [JOB_NAMES.RECIPIENT_MASTER]: { create: PERMISSIONS.RECIPIENT_MASTER_CREATE, read: PERMISSIONS.RECIPIENT_MASTER_READ, update: PERMISSIONS.RECIPIENT_MASTER_UPDATE, delete: PERMISSIONS.RECIPIENT_MASTER_DELETE },
  [JOB_NAMES.RECIPIENT_LIST]: { create: PERMISSIONS.RECIPIENT_LIST_CREATE, read: PERMISSIONS.RECIPIENT_LIST_READ, update: PERMISSIONS.RECIPIENT_LIST_UPDATE, delete: PERMISSIONS.RECIPIENT_LIST_DELETE },
  [JOB_NAMES.ACTION_MASTER]: { create: PERMISSIONS.ACTION_MASTER_CREATE, read: PERMISSIONS.ACTION_MASTER_READ, update: PERMISSIONS.ACTION_MASTER_UPDATE, delete: PERMISSIONS.ACTION_MASTER_DELETE },
  [JOB_NAMES.SCHEDULE_MASTER]: { create: PERMISSIONS.SCHEDULE_CREATE, read: PERMISSIONS.SCHEDULE_READ, update: PERMISSIONS.SCHEDULE_UPDATE, delete: PERMISSIONS.SCHEDULE_DELETE },
  [JOB_NAMES.NOTIFICATION_RUNS]: { read: PERMISSIONS.NOTIFICATION_READ },
};

/** The permission key for a job + capability, or null if the job grants no such action. */
export const permissionKeyFor = (jobName: string, action: PermissionAction): string | null =>
  JOB_PERMISSIONS[jobName]?.[action] ?? null;

/**
 * True if the user holds ANY permission for the job (create/read/update/delete) —
 * used to decide whether to show a screen's menu entry.
 */
export const hasAnyPermissionForJob = (jobName: string, userKeys: Set<string>): boolean => {
  const perms = JOB_PERMISSIONS[jobName];
  if (!perms) return false;
  return Object.values(perms).some((key) => !!key && userKeys.has(key));
};
